import useLocalStorage from "@/hooks/useLocalStorage";
import useTraces from "@/hooks/useTraces";
import useGeoJsonShapeFiles from "@/services/store/useGeoJsonShapeFiles";
import { logger } from "@/utils/functions/logger";
import { ForkLeft, Layers } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PublishIcon from "@mui/icons-material/Publish";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import SatelliteAltOutlinedIcon from "@mui/icons-material/SatelliteAltOutlined";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChangeEvent, FC, useEffect, useState } from "react";
import useUsers from "@/hooks/useUsers";
import { hasPermission } from "@/utils/functions/permission";
import { Permission } from "@/utils/constants/permission";


interface TopLeftPanelProps {
  // eslint-disable-next-line no-unused-vars
  onTraceEdit: (enabled: boolean) => void;
  onTraceEditSave: () => void;
  onTraceEditCancel: () => void;

  // eslint-disable-next-line no-unused-vars
  onToggleSatelliteView: (value: boolean) => void;
}

const TopLeftPanel: FC<TopLeftPanelProps> = ({
  onTraceEdit,
  onTraceEditSave,
  onTraceEditCancel,
  onToggleSatelliteView: onToggleSatelliteViewProps,
}) => {
  const { supervisor } = useUsers();
  const { fetchGeoJsonFiles, selectGeoJsonFile } = useGeoJsonShapeFiles((state) => state);

  const { createTrace, addTraceToUser } = useTraces();

  const [layersAnchorEl, setLayersAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [traceEditAnchorEl, setTraceEditAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [traceEditEnabled, setTraceEditEnabled] = useState(false);
  const geoJsonFiles = useGeoJsonShapeFiles((state) => state.geoJsonFiles);
  const [satelitteViewToggled, setSatelitteViewToggled] = useLocalStorage("satelitteViewToggled", false);

  const handleTraceImport = (mouseEvent: React.MouseEvent<HTMLButtonElement>) => {
    mouseEvent.stopPropagation();

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        const [file] = event.target.files;
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target && e.target.result) {
            try {
              const content: string = e.target.result as string;

              const trace: Trace = JSON.parse(content);

              const [userLabel] = file.name.split("_");

              if (userLabel) addTraceToUser(trace, userLabel);
              else createTrace(trace);
            } catch (e) {
              logger.error(e);
            }
          }
        };
        reader.readAsText(file);
      }
    };

    const inputElement = document.createElement("input");
    inputElement.hidden;
    inputElement.type = "file";
    inputElement.accept = "*.json";
    inputElement.addEventListener("change", (e: Event) => {
      handleFileChange(e as unknown as ChangeEvent<HTMLInputElement>); // forced cast
    });

    window.setTimeout(() => {
      inputElement.click();
    }, 100);
  };

  const handleLayerIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setLayersAnchorEl((state) => (state ? null : (e.target as HTMLButtonElement)));
  };

  // Trace edit button handling
  const handleTraceEditIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setTraceEditEnabled(!traceEditEnabled);
    setTraceEditAnchorEl((state) => (state ? null : (e.target as HTMLButtonElement)));
  };

  const handleTraceEditSaveClick = () => {
    setTraceEditEnabled(false);
    setTraceEditAnchorEl(null);
    onTraceEditSave();
  };

  const handleTraceEditCancelClick = () => {
    setTraceEditEnabled(false);
    setTraceEditAnchorEl(null);
    onTraceEditCancel();
  };

  const onToggleSatelliteView = () => {
    setSatelitteViewToggled(!satelitteViewToggled);
  };

  useEffect(() => {
    onToggleSatelliteViewProps(satelitteViewToggled);
  }, [satelitteViewToggled]);

  useEffect(() => {
    onTraceEdit(traceEditEnabled);
  }, [traceEditEnabled]);

  useEffect(() => {
    fetchGeoJsonFiles();
  }, []);

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          zIndex: 600,
          top: 15,
          width: 35,
          left: 10,
          m: 0,
        }}>
        <Stack
          divider={
            <Box
              sx={{
                height: "1px",
                width: "100%",
                background: (theme: ThemeType) => theme.palette.primary.contrastText,
              }}
            />
          }
          sx={{
            background: (theme: ThemeType) => theme.palette.secondary.main,
            borderRadius: 4,
          }}
          spacing={0.5}>
          {supervisor != null && hasPermission(Permission.IMPORT_TRACE, supervisor) && (
            <Tooltip title="Importer une trace" placement="right">
              <IconButton onMouseDown={handleTraceImport} sx={{ color: "white" }}>
                <PublishIcon />
              </IconButton>
            </Tooltip>
          )}


          {supervisor != null && hasPermission(Permission.CREATE_TRACE, supervisor) && (
            <Tooltip title="Créer une trace" placement="right">
              <IconButton onMouseDown={handleTraceEditIconClick} sx={{ color: "white" }}>
                {traceEditEnabled ? <ForkLeft sx={{ color: "green" }} /> : <ForkLeft />}
              </IconButton>
            </Tooltip>
          )}

          {supervisor != null && hasPermission(Permission.MODIFY_LAYER, supervisor) && (
            <Tooltip title="Modifier les couches" placement="right">
              <IconButton onMouseDown={handleLayerIconClick} sx={{ color: "white" }}>
                <Layers />
              </IconButton>
            </Tooltip>
          )}
          {supervisor != null && hasPermission(Permission.SWITCH_LAYER, supervisor) && (
            <Tooltip title="changer de vue" placement="right">
              <IconButton onMouseDown={onToggleSatelliteView} sx={{ color: "white" }}>
                {satelitteViewToggled ? <SatelliteAltIcon /> : <SatelliteAltOutlinedIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* Trace edit popper */}
      <Popper open={Boolean(traceEditAnchorEl)} anchorEl={traceEditAnchorEl} placement="right">
        <Box
          sx={{
            background: (theme: ThemeType) => theme.palette.secondary.main,
            borderRadius: 2,
            padding: "0px 8px",
            ml: 1,
          }}>
          <CheckIcon
            onClick={handleTraceEditSaveClick}
            sx={{ cursor: "pointer", color: (theme: ThemeType) => theme.palette.primary.contrastText, m: 0.5 }}
          />
          <CloseIcon
            onClick={handleTraceEditCancelClick}
            sx={{ cursor: "pointer", color: (theme: ThemeType) => theme.palette.primary.contrastText, m: 0.5 }}
          />
        </Box>
      </Popper>

      {/* Layers popper */}
      <Popper open={Boolean(layersAnchorEl)} anchorEl={layersAnchorEl} placement="right-start">
        <Box
          sx={{
            background: (theme: ThemeType) => theme.palette.secondary.main,
            borderRadius: 2,
            padding: "0px 8px",
            ml: 1,
          }}>
          <FormGroup>
            {Object.keys(geoJsonFiles).map((file) => (
              <FormControlLabel
                key={file}
                control={
                  <Checkbox
                    checked={geoJsonFiles[file]}
                    onChange={() => selectGeoJsonFile(file)}
                    sx={{ color: (theme: ThemeType) => theme.palette.primary.contrastText }}
                  />
                }
                label={
                  <Typography sx={{ color: (theme: ThemeType) => theme.palette.primary.contrastText }}>
                    {file}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Box>
      </Popper>
    </>
  );
};

export default TopLeftPanel;
